import PropTypes from 'prop-types';
import React, { Component } from 'react';
import CamTimeout from '@surfline/quiver-react/dist/CamTimeout';
import { Spinner } from '@surfline/quiver-react';
import classNames from 'classnames';

import config from '../../config';

import { SL_WEB_AD_FREE_MODAL } from '../../common/treatments';

import CTAProvider from '../CTAProvider';

import SpotReportCam from '../../components/SpotReportCamV2';
import NearbyCams from '../../components/NearbyCams';
import SpotReportCamHeaderCTA from '../../components/SpotReportCamHeaderCTA';

import devicePropType from '../../propTypes/device';
import spotReportDataPropType from '../../propTypes/spot';
import userDetailsPropTypes from '../../propTypes/userDetails';
import multiCamPropType from '../../propTypes/multiCam';

import CamSelectorMenu from '../../components/CamSelectorMenu';
import MultiCamPaywall from '../../components/MultiCamPaywall';
import getCamStillImage from '../../utils/getCamStillImage';

import getUserType from '../../utils/getUserType';
import locationSearchToObject from '../../utils/locationSearchToObject';

/*
cam error message type options
SUB - Top message + secondary message below
MSG - Top message only
ALT - Single word message for small cam displays
*/
const SUB = 'SUB';
const MSG = 'MSG';
const ALT = 'ALT';

const spotReportCamerasClassName = (isMultiCam, numCamsDisplayed, isPremium, multiCamTimeout) =>
  classNames({
    'sl-spot-report-page__cameras': true,
    'sl-spot-report-page__cameras--multi-cam': isMultiCam,
    'sl-spot-report-page__cameras--single-cam': isMultiCam && isPremium && numCamsDisplayed === 1,
    'sl-spot-report-page__cameras--quad-cam': isMultiCam && numCamsDisplayed === 4,
    'sl-spot-report-page__cameras--multi-timeout': multiCamTimeout,
  });

const camContainerClassName = (isPrimary, camNum, showPaywall) =>
  classNames({
    'sl-spot-report-page-multi-cam__secondary': !isPrimary,
    'sl-spot-report-page-multi-cam__primary': isPrimary,
    [`sl-spot-report-page-multi-cam__secondary--${camNum + 1}`]: !isPrimary,
    'sl-spot-report-page-multi-cam__paywall': showPaywall,
  });

const multiCamLoadingClassName = numCams =>
  classNames({
    'sl-multi-cam-loading': true,
    'sl-multi-cam-loading--double': numCams === 2,
    'sl-multi-cam-loading--triple': numCams === 3,
    'sl-multi-cam-loading--quad': numCams === 4,
  });

const multiCamContainerClassName = numCamsDisplayed =>
  classNames({
    'sl-spot-report-page-multi-cam': true,
    'sl-spot-report-page-multi-cam--single': numCamsDisplayed === 1,
    'sl-spot-report-page-multi-cam--double': numCamsDisplayed === 2,
    'sl-spot-report-page-multi-cam--triple': numCamsDisplayed === 3,
    'sl-spot-report-page-multi-cam--quad': numCamsDisplayed === 4,
  });

class SpotReportCams extends Component {
  constructor(props) {
    super(props);
    const { isTheatreMode, multiCam, isPremium, isMultiCam } = this.props;
    const singlePlayerCamId = this.getSinglePlayerCamId();
    this.state = {
      numCamsDisplayed:
        isTheatreMode && multiCam.activeView === 'MULTI' && !singlePlayerCamId
          ? multiCam.numCams
          : 1,
      multiCamTimeout: false,
      multiCamIsLoading: !!isMultiCam && isPremium,
    };
  }

  componentDidUpdate(prevProps) {
    const { multiCam, isPremium, device } = this.props;
    const singlePlayerCamId = this.getSinglePlayerCamId();
    const didCalculateDeviceSize = !prevProps.device.width && device.width;
    const isDesktopOrTablet = device.width >= 512;
    const defaultToMulti = multiCam.isMultiCam && isDesktopOrTablet && isPremium;
    const defaultToSingle = !!singlePlayerCamId;

    // didCalculateDeviceSize ensures we only call on mount since device.width
    // is only undefined on mount and then defined after state is set for the HOC
    if (didCalculateDeviceSize) {
      if (defaultToMulti && !defaultToSingle) {
        this.doChangeCamView('MULTI');
      } else if (defaultToSingle) {
        this.doChangeCamView('SINGLE');
      }
      this.completeMultiCamLoading();
    }
  }

  getSinglePlayerCamId = () => {
    const { location } = this.props;
    const urlParams = location && locationSearchToObject(location.search);
    return urlParams && urlParams.camId;
  };

  completeMultiCamLoading = () => this.setState({ multiCamIsLoading: false });

  timeoutMultiCams = () => {
    this.setState({
      multiCamTimeout: true,
    });
  };

  resumeMultiCams = () =>
    this.setState({
      multiCamTimeout: false,
    });

  doChangeCamView = activeView => {
    const { multiCam, doSetCamView } = this.props;
    const numCamsDisplayed = multiCam.numCams;
    doSetCamView(activeView);

    if (activeView === 'MULTI') {
      return this.setState({
        numCamsDisplayed,
      });
    }

    return this.setState({
      numCamsDisplayed: 1,
    });
  };

  doSetPrimaryCamAndHistory = cam => {
    const { doSetPrimaryCam, history } = this.props;
    doSetPrimaryCam(cam);
    const singlePlayerCamId = this.getSinglePlayerCamId();
    if (history && !!singlePlayerCamId) history.push(`?camId=${cam._id}`);
  };

  render() {
    const {
      isMultiCam,
      multiCam,
      spotId,
      legacySpotId,
      spot,
      isTheatreMode,
      reportData,
      userDetails,
      isPremium,
      doSetPrimaryCam,
      doTrackEvent,
      headerCTASegmentProperties,
      device,
      doLoadTreatment,
      entitlements,
    } = this.props;
    const singlePlayerCamId = this.getSinglePlayerCamId();
    const userType = getUserType(entitlements, !!userDetails);
    const adFreePopupTreatment = userType === 'REGISTERED' && doLoadTreatment(SL_WEB_AD_FREE_MODAL);
    let adFreePopupText = null;
    if (adFreePopupTreatment === 'Free_User_Skip_Trial') {
      adFreePopupText = {
        accept: '15-Days Free',
        decline: 'Skip Trial',
        message: 'Stream cams without the ads.',
        headline: 'Surfline Premium',
      };
    } else if (adFreePopupTreatment === 'Free_User_No_Thanks') {
      adFreePopupText = {
        accept: 'START FREE TRIAL',
        decline: 'NO THANKS',
        message: 'Stream cams without the ads.',
        headline: 'Surfline Premium',
      };
    }

    const subregionId = spot && spot.subregion._id;

    const spotReportCamProps = {
      spotId,
      legacySpotId,
      spot,
      reportData,
      userDetails,
      isPremium,
      adFreePopupText,
    };

    const multiCamPaywallSegmentProperties = {
      ctaLocation: 'multi cam view',
      category: 'cams & reports',
      pageName: 'Spot Report',
      subregionId,
      spotId,
      spotName: spot.name,
    };

    const singlePlayerCam = spot.cameras.filter(cam => cam._id === singlePlayerCamId);
    const isSingleCamAssignedToSpot = singlePlayerCam.length > 0;
    if (multiCam.primaryCam._id !== singlePlayerCamId && isSingleCamAssignedToSpot)
      doSetPrimaryCam(singlePlayerCam[0]);

    const { numCamsDisplayed, multiCamTimeout, multiCamIsLoading } = this.state;
    const showPaywall = isMultiCam && multiCam.activeView === 'MULTI' && !isPremium;
    const displayedCams =
      multiCam.activeView === 'MULTI'
        ? multiCam.initialCameras.slice(0, numCamsDisplayed)
        : [multiCam.cameras[0]];
    const numberOfCams = displayedCams.length;

    const isMobile = device.width <= config.mobileWidth;
    const isTablet = device.width <= config.tabletLargeWidth && !isMobile;
    let messageType = isMobile || isTablet ? MSG : SUB;

    return (
      <div
        className={spotReportCamerasClassName(
          isMultiCam,
          numCamsDisplayed,
          isPremium,
          multiCamTimeout
        )}
      >
        {!isPremium && !isTheatreMode ? (
          <div className="sl-spot-report-cam__cta">
            <CTAProvider segmentProperties={headerCTASegmentProperties}>
              <SpotReportCamHeaderCTA />
            </CTAProvider>
          </div>
        ) : null}
        {isMultiCam && multiCamIsLoading && (
          <div className={multiCamLoadingClassName(multiCam.cameras.length)}>
            <Spinner />
          </div>
        )}
        {isMultiCam && !multiCamIsLoading && (
          <>
            <CamSelectorMenu
              cameras={multiCam.cameras}
              isPremium={isPremium}
              primaryCam={multiCam.primaryCam}
              numCams={multiCam.numCams}
              activeView={multiCam.activeView}
              initialCameras={multiCam.initialCameras}
              doSetPrimaryCam={cam => this.doSetPrimaryCamAndHistory(cam)}
              doSetCamView={this.doChangeCamView}
              doTrackEvent={doTrackEvent}
              spotId={spotId}
              subregionId={spot && spot.subregion._id}
              userDetails={userDetails}
            />
            <div className={multiCamContainerClassName(numCamsDisplayed)}>
              {multiCamTimeout && <CamTimeout isMultiCam onClick={this.resumeMultiCams} />}
              {displayedCams.map(cam => {
                const isPrimaryCam = cam._id === multiCam.primaryCam._id || numCamsDisplayed === 1;
                const camNum = multiCam.cameras.findIndex(orderedCam => orderedCam._id === cam._id);
                const camStillUrl = getCamStillImage(cam);

                // specific messageType for multi cam configs
                if (numberOfCams % 2 === 0 && !isTablet) {
                  messageType = isMobile ? ALT : SUB;
                }

                if (numberOfCams === 3) {
                  if (!isPrimaryCam) {
                    messageType = isMobile || isTablet ? ALT : MSG;
                  } else if (isMobile) {
                    messageType = ALT;
                  }
                }

                return (
                  <div
                    key={cam._id}
                    className={camContainerClassName(isPrimaryCam, camNum, showPaywall)}
                  >
                    {showPaywall || multiCamTimeout ? (
                      <div
                        className="sl-spot-report-page-multi-cam__camStill"
                        style={{ backgroundImage: `url(${camStillUrl})` }}
                      />
                    ) : (
                      <SpotReportCam
                        isMultiCam
                        key={cam._id}
                        playerId={`sl-spot-report-cam-${cam._id}`}
                        camera={cam}
                        numCamsDisplayed={numCamsDisplayed}
                        isPersistentCam={isPrimaryCam}
                        multiCamTimeout={multiCamTimeout}
                        isPrimaryCam={isPrimaryCam}
                        messageType={messageType}
                        timeoutMultiCams={this.timeoutMultiCams}
                        resumeMultiCams={this.resumeMultiCams}
                        {...spotReportCamProps}
                      />
                    )}
                  </div>
                );
              })}
              {showPaywall && (
                <CTAProvider segmentProperties={multiCamPaywallSegmentProperties}>
                  <MultiCamPaywall />
                </CTAProvider>
              )}
            </div>
          </>
        )}
        {!isMultiCam && (
          <SpotReportCam
            isPersistentCam
            camera={isSingleCamAssignedToSpot ? singlePlayerCam[0] : spot.cameras[0]}
            messageType={messageType}
            {...spotReportCamProps}
          />
        )}
        {!isTheatreMode && <NearbyCams spotId={spotId} />}
      </div>
    );
  }
}

SpotReportCams.propTypes = {
  doSetCamView: PropTypes.func.isRequired,
  multiCam: multiCamPropType.isRequired,
  isPremium: PropTypes.bool,
  isTheatreMode: PropTypes.bool,
  spot: PropTypes.shape({
    name: PropTypes.string,
    subregion: PropTypes.shape({
      _id: PropTypes.string,
      forecastStatus: PropTypes.string,
    }),
    breadcrumb: PropTypes.array,
    cameras: PropTypes.array,
  }),
  spotId: PropTypes.string.isRequired,
  legacySpotId: PropTypes.string.isRequired,
  userDetails: userDetailsPropTypes,
  reportData: spotReportDataPropType,
  doSetPrimaryCam: PropTypes.func.isRequired,
  doTrackEvent: PropTypes.func.isRequired,
  device: devicePropType.isRequired,
  headerCTASegmentProperties: PropTypes.shape({}).isRequired,
  isMultiCam: PropTypes.bool,
  doLoadTreatment: PropTypes.func.isRequired,
  entitlements: PropTypes.arrayOf(PropTypes.string),
  singlePlayerCamId: PropTypes.string,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  location: PropTypes.shapeOf({
    search: PropTypes.string,
  }).isRequired,
};

SpotReportCams.defaultProps = {
  spot: null,
  isPremium: false,
  userDetails: null,
  reportData: null,
  isTheatreMode: false,
  isMultiCam: false,
  entitlements: [],
  singlePlayerCamId: null,
};

export default SpotReportCams;
